import type { PrototypeRuntime } from "./prototypeTypes";
import type { AiCourseListResponse, AiCourseSummary } from "./apiContracts";
import { byId } from "./domUtils";
import { handleApiError } from "./authGuards";
import { renderPagedInfiniteList, renderProgressiveList } from "./progressiveList";
import { buildTopicCard, observeAdaptiveCategoryBudget, type TopicCardInput } from "./topicCard";
import { aiJobCardElement } from "./completedCardTemplate";
import { renderEmptyStateMarkup } from './emptyStateMarkup';
import { fallbackKeywordCoefficient } from '../../shared/keywordWeight';
import { AI_CHAPTER_AUTO_PLAN_LABEL, AI_CHAPTER_AUTO_PLAN_PRESET } from '../../domain/aiChapterTargetPolicy';
import { suspendAiCreateProgressPolling, type AiCreateProgressPollingState } from './aiCreateResumeController';
import { shouldCommitTextInputOnEnter } from './inputComposition';

interface AiCourseHolder {
	__aiCourses?: AiCourseSummary[];
	__aiCourseTopicMeta?: Record<string, { title: string; keywords?: Array<{ keyword: string; coefficient: number }> }>;
	__aiCourseListOwner?: object;
}

/** Days since the given ISO timestamp, floored to 0. */
function relativeDays(iso: string): number {
	return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
}

function isNewThisWeek(iso: string): boolean {
	return Date.now() - new Date(iso).getTime() < 7 * 86400000;
}

/** Parse a chapter-count prompt ("5" / "3-5" / "") to a display label. */
function chapterEstimate(raw: string): string {
	if (raw === AI_CHAPTER_AUTO_PLAN_PRESET) return AI_CHAPTER_AUTO_PLAN_LABEL;
	const matches = raw.match(/\d+/g);
	if (!matches || matches.length === 0) return "~8";
	if (matches.length === 1) return matches[0];
	return `${Math.min(...matches.map(Number))}-${Math.max(...matches.map(Number))}`;
}

/** Relative day label for horizontal cards: "Today" / "3d ago" */
function relativeDayLabel(iso: string): string {
	const days = relativeDays(iso);
	if (days === 0) return "Today";
	if (days === 1) return "Yesterday";
	return `${days}d ago`;
}

/**
 * The description field stores the full brief, whose first line is the title
 * (see deriveAiTitle in aiJobSubmission.ts). When using the description as a
 * subtitle fallback, strip the leading title line so the card doesn't show
 * the same text in both the title and subtitle slots.
 */
function stripTitleFromDescription(title: string, description: string): string {
	if (!description) return "";
	const titleTrimmed = title.trim();
	if (!titleTrimmed) return description;
	const descTrimmed = description.trimStart();
	if (descTrimmed.startsWith(titleTrimmed)) {
		const remainder = descTrimmed.slice(titleTrimmed.length).trim();
		return remainder || description;
	}
	return description;
}

function buildCardElement(course: AiCourseSummary, filter: AiCreateFilter): HTMLElement {
	// Defensive: completed but topic is missing/deleted - skip rendering.
	// The server filters these out, but this prevents any edge case from
	// producing a horizontal row card in the multi-column completed grid.
	if (filter === "completed" && !course.topicSlug) {
		return document.createElement("div");
	}
	if (filter === "completed" && course.topicSlug) {
		const card = buildTopicCard({
			slug: course.topicSlug,
			title: course.title,
			subtitle: course.subtitle,
			category: course.category,
			classification: course.classification,
			coverImageUrl: course.coverImageUrl,
			coverGradient: "grad-ai",
			chapterCount: course.chapterCount,
			audioMinutes: course.audioMinutes,
			openCount: course.openCount,
			favoriteCount: course.favoriteCount,
			voteValue: course.voteValue ?? 0,
			isFavorite: course.isFavorite ?? false,
			createdAt: course.createdAt,
			isNewThisWeek: isNewThisWeek(course.createdAt),
		} as TopicCardInput, {
			showSubtitle: true,
			auxiliaryAction: true,
			adaptiveCategories: true,
			variant: 'mobile-compact',
		});
		// AI Create completed cards: the footer aux slot becomes an icon-only
		// Submission button that opens the reader dialog (same shell as the
		// Community proposal dialog) instead of the standalone job page.
		// The delegated click handler uses closest("[data-proto-click]")
		// which finds the .topic-card-aux-action span (nearest ancestor with the
		// attribute) before reaching the card's own showTopicDetail action,
		// so no stopPropagation is needed.
		const linkEl = card.querySelector<HTMLElement>(".topic-card-aux-action");
		if (linkEl) {
			linkEl.innerHTML = SUBMISSION_ICON_SVG;
			linkEl.setAttribute("data-proto-click", `openAiSubmissionDialog('${escapeAttr(course.id)}')`);
			linkEl.setAttribute('role', 'button');
			linkEl.setAttribute('tabindex', '0');
			linkEl.setAttribute('aria-label', `View submission for ${course.title}`);
			linkEl.setAttribute('title', 'Submission');
			linkEl.dataset.keyboardAction = 'true';
			linkEl.classList.add("ai-submission-link");
			linkEl.style.cursor = "pointer";
		}
		observeAdaptiveCategoryBudget(card);
		return card;
	}
	const descFallback = stripTitleFromDescription(course.title, course.description).slice(0, 120);
	// In-progress / failed: horizontal row card (one per line)
	// Subtitle fallback: when the user didn't fill in a subtitle, show the
	// "What do you want to learn?" description instead. But the description
	// is the full brief whose first line is the title (see deriveAiTitle),
	// so strip the title prefix to avoid showing the title twice.
	return aiJobCardElement({
		jobId: course.id,
		title: course.title,
		subtitle: course.subtitle || descFallback,
		// 优先展示用户提交时选择的原始范围（如 "9-12"）；旧任务没有该列时回退到执行值
		chapterEstimate: chapterEstimate(course.requestedChaptersRange ?? course.requestedChapters),
		linksCount: course.linksCount,
		attachmentsCount: course.attachmentsCount,
		createdAt: course.createdAt,
		progress: course.progress ?? null,
		status: course.status,
		topicAvailable: Boolean(course.topicSlug),
	});
}

function emptyState(filter: "inprogress" | "completed", search = ''): string {
	if (search.trim()) {
		return renderEmptyStateMarkup({ icon: 'search', title: 'No matching topics', subtitle: 'Try a different search or clear the current query.', extraClass: 'ai-created-empty' });
	}
	if (filter === "inprogress") {
		return renderEmptyStateMarkup({ icon: 'sparkles', title: 'No topics generating', subtitle: 'Use the Create private topic button above to begin.', extraClass: 'ai-created-empty' });
	}
	return renderEmptyStateMarkup({ icon: 'book', title: 'No completed topics yet', subtitle: 'Your finished private topics will appear here.', extraClass: 'ai-created-empty' });
}

export type AiCreateFilter = "inprogress" | "completed";

export async function hydrateAiCreateView(
	runtime: PrototypeRuntime,
	root: HTMLElement | Document,
	filter: AiCreateFilter = "completed",
): Promise<void> {
	const list = byId(root, "aiCreatedList");
	if (!list) return;
	// P4.3: ai.history.read 只需 session（spec §8.4）
	// 所有登录用户（Free/Lite/Pro/Max）都能看自己的历史列表
	// AI Create 生成能力（ai.course.create）仍维持 Pro+ 门槛，
	// 在提交按钮的 click 守卫处拦截，不影响历史列表读取。
	// Persist runtime FIRST so applyAiCreateFilter (capsule switch) always
	// has a valid runtime ref, even when the dedupe gate trips below.
	(root as unknown as { __aiRuntime?: PrototypeRuntime }).__aiRuntime = runtime;

	// Global per-view dedupe: showView() and shellActions both re-fire
	// this hydrator on the same navigation. Only the first call within
	// the debounce window proceeds; capsule switches bypass this gate
	// via fetchAndRenderAiCourses directly.
	const { shouldHydrate } = await import("./hydrateGuard");
	if (!shouldHydrate("ai-create")) return;

	await fetchAndRenderAiCourses(runtime, root, filter, currentAiSearch(root));
}

export async function fetchAndRenderAiCourses(
	runtime: PrototypeRuntime,
	root: HTMLElement | Document,
	filter: AiCreateFilter = "completed",
	search: string = "",
): Promise<void> {
	const settlePolling = suspendAiCreateProgressPolling(root);
	const list = byId(root, "aiCreatedList");
	if (!list) {
		settlePolling(undefined);
		return;
	}
	const holder = root as unknown as AiCourseHolder;
	holder.__aiCourseListOwner = {};
	list.classList.toggle("ai-created-list--rows", filter === "inprogress");
	const skeletonClassName = filter === "completed" ? "topic-card-compact-skeleton" : "ai-created-skeleton";
	list.innerHTML = renderSkeleton(skeletonClassName);
	holder.__aiCourses = [];
	holder.__aiCourseTopicMeta = holder.__aiCourseTopicMeta ?? {};
	wireAiSearch(root);
	let firstResponse: AiCourseListResponse | null = null;
	let firstResponseCommitted = false;
	let firstPageSettled = false;
	const settleFirstPage = (nextState?: AiCreateProgressPollingState | null) => {
		if (firstPageSettled) return;
		firstPageSettled = true;
		settlePolling(nextState);
	};
	renderPagedInfiniteList({
		container: list,
		loadPage: async (cursor) => {
			try {
				const response = await runtime.api.listAiCourses(filter, { limit: 20, cursor, search });
				if (cursor === null) firstResponse = response;
				return { items: response.courses ?? [], hasMore: response.hasMore, nextCursor: response.nextCursor ?? null };
			} catch (error) {
				if (cursor === null) settleFirstPage(undefined);
				delete list.dataset.quotaAvailable;
				handleApiError(runtime, "free", false, error, { root: root as HTMLElement, intent: "ai" });
				throw error;
			}
		},
		renderItem: (course) => buildCardElement(course, filter),
		skeletonClassName,
		skeletonCount: 3,
		endMessage: "You've reached the end of your AI-created topics.",
		emptyElement: htmlToElement(emptyState(filter, search)),
		onItemsChange: (courses) => {
			if (!firstResponseCommitted && firstResponse) {
				firstResponseCommitted = true;
				updateAiQuota(root, list, firstResponse);
				settleFirstPage(filter === 'inprogress' ? progressPollingState(firstResponse) : null);
			}
			updateAiCourseCache(holder, courses);
		},
	});
}

/** Refreshes visible In Progress cards; returns null when a newer list owns the root. */
export async function refreshAiCreateProgressSnapshot(
	runtime: PrototypeRuntime,
	root: HTMLElement | Document,
	search: string = currentAiSearch(root),
): Promise<AiCreateProgressPollingState | null> {
	const list = byId(root, 'aiCreatedList');
	if (!list) return { activeCount: 0, latestActiveCreatedAt: null, serverNow: new Date().toISOString() };
	const holder = root as unknown as AiCourseHolder;
	const ownerAtRequest = holder.__aiCourseListOwner;
	const response = await runtime.api.listAiCourses('inprogress', { limit: 20, search });
	const courses = [...(response.courses ?? [])];
	let nextCursor = response.nextCursor ?? null;
	let hasMore = response.hasMore;
	const seenCursors = new Set<string>();
	while (hasMore && nextCursor && !seenCursors.has(nextCursor)) {
		seenCursors.add(nextCursor);
		const page = await runtime.api.listAiCourses('inprogress', { limit: 20, cursor: nextCursor, search });
		courses.push(...(page.courses ?? []));
		hasMore = page.hasMore;
		nextCursor = page.nextCursor ?? null;
	}
	if (holder.__aiCourseListOwner !== ownerAtRequest) return null;
	holder.__aiCourseListOwner = {};

	list.classList.add('ai-created-list--rows');
	renderProgressiveList({
		container: list,
		items: courses,
		renderItem: (course) => buildCardElement(course, "inprogress"),
		skeletonClassName: 'ai-created-skeleton',
		skeletonCount: 3,
		endMessage: "You've reached the end of your AI-created topics.",
		emptyElement: htmlToElement(emptyState('inprogress', search)),
		immediate: true,
	});
	updateAiQuota(root, list, response);
	updateAiCourseCache(holder, courses);
	return {
		activeCount: response.activeCount,
		latestActiveCreatedAt: response.latestActiveCreatedAt,
		serverNow: response.serverNow,
	};
}

function progressPollingState(response: AiCourseListResponse): AiCreateProgressPollingState | null {
	if (response.activeCount <= 0) return null;
	return {
		activeCount: response.activeCount,
		latestActiveCreatedAt: response.latestActiveCreatedAt,
		serverNow: response.serverNow,
	};
}

function updateAiQuota(root: HTMLElement | Document, list: HTMLElement, response: AiCourseListResponse): void {
	if (!response.quota) {
		delete list.dataset.quotaAvailable;
		return;
	}
	const usedEl = byId(root, 'aiQuotaUsed');
	const totalEl = byId(root, 'aiQuotaTotal');
	if (usedEl) usedEl.textContent = String(response.quota.used);
	if (totalEl) totalEl.textContent = String(response.quota.limit);
	list.dataset.quotaAvailable = String(response.quota.remaining);
}

function updateAiCourseCache(holder: AiCourseHolder, courses: AiCourseSummary[]): void {
	holder.__aiCourses = courses;
	holder.__aiCourseTopicMeta = holder.__aiCourseTopicMeta ?? {};
	for (const course of courses) {
		const slug = course.topicSlug ?? course.id;
		holder.__aiCourseTopicMeta[slug] = {
			title: course.title,
			keywords: buildCourseKeywords(course),
		};
	}
}

/** Read the current AI Create search input value. */
function currentAiSearch(root: HTMLElement | Document): string {
	const input = byId(root, "aiSearchInput") as HTMLInputElement | null;
	return (input?.value ?? "").trim();
}

/** Switch capsule: re-fetch from the API with the new status filter + current search. */
export function applyAiCreateFilter(
	root: HTMLElement | Document,
	filter: AiCreateFilter = "completed",
): void {
	const holder = root as unknown as { __aiRuntime?: PrototypeRuntime };
	const runtime = holder.__aiRuntime;
	if (!runtime) return;
	void fetchAndRenderAiCourses(runtime, root, filter, currentAiSearch(root));
}

/** Wire the AI Create search input. */
function wireAiSearch(root: HTMLElement | Document): void {
	const input = byId(root, "aiSearchInput") as HTMLInputElement | null;
	if (!input || input.dataset.bound === "true") return;
	input.dataset.bound = "true";
	input.addEventListener("input", () => toggleAiClearButton(root));
	input.addEventListener("keydown", (event) => {
		if (shouldCommitTextInputOnEnter(event)) {
			event.preventDefault();
			if (typeof input.blur === 'function') input.blur();
			runAiSearchInternal(root);
		}
	});
}

function toggleAiClearButton(root: HTMLElement | Document): void {
	const input = byId(root, "aiSearchInput") as HTMLInputElement | null;
	const clearBtn = byId(root, "aiSearchClear") as HTMLButtonElement | null;
	if (!input || !clearBtn) return;
	clearBtn.style.display = input.value.trim() ? "" : "none";
}

function runAiSearchInternal(root: HTMLElement | Document): void {
	toggleAiClearButton(root);
	const holder = root as unknown as { __aiRuntime?: PrototypeRuntime };
	const runtime = holder.__aiRuntime;
	if (!runtime) return;
	const filter = currentAiFilter(root);
	void fetchAndRenderAiCourses(runtime, root, filter, currentAiSearch(root));
}

export function currentAiFilter(root: HTMLElement | Document): AiCreateFilter {
	const active = (root instanceof Document ? root : root.ownerDocument ?? document)
		.querySelector<HTMLElement>("#aiFilterTabs .category-pill.active");
	const click = active?.dataset.protoClick ?? "";
	if (click.includes("inprogress")) return "inprogress";
	return "completed";
}

export function runAiSearch(root: HTMLElement | Document): void {
	runAiSearchInternal(root);
}

export function clearAiSearch(root: HTMLElement | Document): void {
	const input = byId(root, "aiSearchInput") as HTMLInputElement | null;
	if (input) input.value = "";
	toggleAiClearButton(root);
	runAiSearchInternal(root);
}

function buildCourseKeywords(course: AiCourseSummary): Array<{ keyword: string; coefficient: number }> {
	const words = course.title.match(/[A-Za-z0-9][A-Za-z0-9+-]{1,}|[\u4e00-\u9fff]{2,}/g) ?? [];
	const base = words.length ? words : ["ai", "create", "private", "course"];
	return base.slice(0, 10).map((keyword, index) => ({
		keyword,
		coefficient: fallbackKeywordCoefficient(index),
	}));
}

function htmlToElement(html: string): HTMLElement {
	const template = document.createElement("template");
	template.innerHTML = html;
	return template.content.firstElementChild as HTMLElement;
}

function renderSkeleton(skeletonClassName: 'topic-card-compact-skeleton' | 'ai-created-skeleton'): string {
	return Array.from({ length: 3 }, () => `<div class="${skeletonClassName}"></div>`).join('');
}

function escapeAttr(s: string): string {
	return s.replace(/[&<>"']/g, (c) =>
		({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] ?? c),
	);
}

// Icon-only "Submission" button glyph (Lucide file-text) on completed cards.
// Sized to match the neighbouring reaction buttons (.topic-actions svg).
const SUBMISSION_ICON_SVG =
	'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="M10 9H8"/><path d="M16 13H8"/><path d="M16 17H8"/></svg>';
